import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Bot, FileText, Image as ImageIcon, KeyRound, Loader2, Paperclip, Play, Square, Sparkles, TriangleAlert, WifiOff, X } from 'lucide-react';
import { fetchAgentHistory, startAgent, streamChat, uploadAttachment, ChatStreamError } from '../../api/playground';
import { useAgentRuntimeStatus, isRuntimeRunning } from '../../hooks/useAgentRuntimeStatus';
import { toast } from '../../utils/toast';
import Markdown from '../ui/Markdown';
import type { ChatMessage, ChatRequest, PlaygroundAgent } from '../../types';

/**
 * 工作台中栏 —— 对话区（练玉坊核心）。
 *
 * Props 契约（后续工作台任务扩展时保持兼容）：
 * - agent: 当前选中的 Agent（未选中时为 null）
 * - initialMessages: 可选；某 Agent 首次打开时预置的历史消息（测试与会话恢复用），
 *   仅在该 Agent 尚无本地消息时生效，一次注入后不再覆盖；提供了它则不拉取后端历史。
 *
 * 交互：
 * - 气泡对话：用户右 / Agent 左，Markdown 渲染，SSE 流式逐 token 上屏 + 光标；
 * - 首次打开某 Agent：先 GET history 恢复历史（有则渲染，替代示例问题气泡）；
 *   无历史（或加载失败）才展示 2-3 个示例问题气泡，点击直接发送；切换 Agent 各载各的；
 * - Agent 未运行：顶部醒目提示条 + 启动服务；启动中显示「正在准备运行时…」；
 * - 输入框：Enter 发送（中文输入法组合期不触发）、Shift+Enter 换行、发送中禁用、停止按钮；
 *   左侧附件按钮（图片 ≤5MB / 文本 txt/md ≤1MB），chip 可移除，发送时先上传再随消息发送；
 * - 错误：429 → 后端中文 detail 原样 toast（未签到 / token 耗尽 / 分钟耗尽 / 预算超限），
 *   额度相关追加专属引导卡（签到或绑定自有 Key）；422/503 → 后端中文提示；网络错误 → 行内重试条；
 * - 助手消息富文本渲染（Markdown/KaTeX/mermaid，见 ui/Markdown）；用户消息纯文本 + 图片内联；
 * - 平台体验模型：status.model_source === 'platform' 时头部显示小徽标。
 */

export interface UiMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
  failed?: boolean;
  /** 本会话内联展示的图片附件（本地 objectURL；历史不含图片，刷新后只剩文本——后端已知取舍） */
  images?: Array<{ name: string; url: string }>;
}

/** 待发送附件（发送时才上传拿 upload_id） */
interface PendingAttachment {
  id: string;
  file: File;
  /** 按扩展名预判（服务端上传后返回的 kind 才是权威） */
  kind: 'image' | 'text';
  /** 图片本地预览 objectURL */
  previewUrl?: string;
}

const IMAGE_EXT = ['.png', '.jpg', '.jpeg', '.webp', '.gif'];
const TEXT_EXT = ['.txt', '.md'];
const ATTACH_ACCEPT = [...IMAGE_EXT, ...TEXT_EXT].join(',');

/** 按扩展名预判附件类型；不在白名单返回 null（服务端也会 415 兜底） */
function detectKind(name: string): 'image' | 'text' | null {
  const lower = name.toLowerCase();
  if (IMAGE_EXT.some((ext) => lower.endsWith(ext))) return 'image';
  if (TEXT_EXT.some((ext) => lower.endsWith(ext))) return 'text';
  return null;
}

function describeChatError(err: unknown): string {
  const e = err as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = e.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return typeof e.message === 'string' ? e.message : '操作失败，请稍后重试';
}

export interface ChatPanelProps {
  agent: PlaygroundAgent | null;
  initialMessages?: Array<{ role: 'user' | 'assistant'; content: string }>;
}

const EXAMPLE_PROMPTS = [
  '介绍一下你自己，以及你能帮我做什么？',
  '用三步教我怎么让 Agent 帮我写周报',
  '给我讲一个 3 句话的温暖小故事',
];

function uid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
}

function toApiMessage(m: UiMessage): ChatMessage {
  return { role: m.role === 'assistant' ? 'assistant' : 'user', content: m.content };
}

export default function ChatPanel({ agent, initialMessages }: ChatPanelProps) {
  const [messagesByAgent, setMessagesByAgent] = useState<Record<string, UiMessage[]>>({});
  const [quotaExhausted, setQuotaExhausted] = useState(false);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const loadedAgentsRef = useRef<Set<string>>(new Set());
  const [starting, setStarting] = useState(false);

  const { status: runtimeStatus, info: runtimeInfo, refresh: refreshStatus } = useAgentRuntimeStatus(agent?.id, { pollMs: 5000 });

  // 首次打开某 Agent 且尚无本地消息时，用 initialMessages 兜底展示（测试/会话恢复用）；
  // 不写入 state，用户一旦发送即把「兜底历史 + 新消息」整体持久化到 messagesByAgent。
  const seededInitial = useMemo<UiMessage[]>(() => {
    if (!initialMessages || initialMessages.length === 0) return [];
    return initialMessages.map((m) => ({ id: uid(), role: m.role, content: m.content }));
  }, [initialMessages]);
  const messages = agent ? (messagesByAgent[agent.id] ?? seededInitial) : [];
  // 历史尚未拉回（messagesByAgent 无该 Agent 记录 + 无 initialMessages 兜底）时展示加载态
  const historyPending =
    !!agent && messagesByAgent[agent.id] === undefined && seededInitial.length === 0;

  // 历史恢复：切换 Agent 时拉取一次后端会话历史（升序），有则渲染、无则示例问题气泡。
  // - 提供了 initialMessages（测试/会话恢复）时直接以其为起始，不拉历史；
  // - 每个 Agent 只拉一次（loadedAgentsRef 记录），用户本地消息永远优先；
  // - 全部 setState 都在 .then/.catch 回调内（规避 react-hooks/set-state-in-effect）。
  useEffect(() => {
    if (!agent || seededInitial.length > 0) return;
    if (loadedAgentsRef.current.has(agent.id)) return;
    let cancelled = false;
    fetchAgentHistory(agent.id)
      .then((res) => {
        if (cancelled) return;
        const msgs = res.messages.map((m) => ({ id: uid(), role: m.role, content: m.content }));
        setMessagesByAgent((prev) => ({ ...prev, [agent.id]: msgs }));
        loadedAgentsRef.current.add(agent.id);
      })
      .catch(() => {
        // 历史拉取失败不阻塞对话：落空列表（回到示例气泡），下次进入不再重试
        if (cancelled) return;
        setMessagesByAgent((prev) => ({ ...prev, [agent.id]: [] }));
        loadedAgentsRef.current.add(agent.id);
      });
    return () => {
      cancelled = true;
    };
  }, [agent, seededInitial]);

  // 自动滚到底部（新消息 / 流式增量）
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messagesByAgent, streaming]);

  const getMessages = useCallback(
    (agentId: string) => messagesByAgent[agentId] ?? [],
    [messagesByAgent]
  );

  const appendToAssistant = (agentId: string, msgId: string, delta: string) => {
    setMessagesByAgent((prev) => {
      const list = prev[agentId] ?? [];
      return {
        ...prev,
        [agentId]: list.map((m) => (m.id === msgId ? { ...m, content: m.content + delta } : m)),
      };
    });
  };

  const finalizeAssistant = (agentId: string, msgId: string, failed = false) => {
    setMessagesByAgent((prev) => {
      const list = prev[agentId] ?? [];
      return {
        ...prev,
        [agentId]: list.map((m) =>
          m.id === msgId ? { ...m, streaming: false, failed } : m
        ),
      };
    });
  };

  /** 返回 true = toast 型错误（不可重试，如额度/预算/配置），false = 可重试错误 */
  const handleChatError = (err: unknown, agentName: string): boolean => {
    if (err instanceof ChatStreamError) {
      if (err.status === 429) {
        // 429 detail 为后端中文提示（未签到 / token 耗尽 / 分钟耗尽 / 预算超限），原样透出
        toast(err.detail || '请求过于频繁，请稍后再试。', 'error');
        // 平台每日额度相关 → 追加专属引导卡（签到或绑定自有 Key）
        if (/额度|签到/.test(err.detail)) setQuotaExhausted(true);
        return true;
      }
      if (err.status === 422) {
        toast(err.detail || '该 Agent 尚未配置完整，请先到右侧补全配置。', 'error');
        return true;
      }
      if (err.status === 503) {
        toast(err.detail || '上游模型服务暂时不可用，请稍后再试。', 'error');
        return true;
      }
      setStreamError(err.detail || `对话失败（HTTP ${err.status}）`);
      return false;
    }
    // 网络 / 其他错误 → 行内可重试
    setStreamError(`与「${agentName}」的连接中断（网络错误），回复可能未完整送达。`);
    return false;
  };

  const runCompletion = async (
    agentId: string,
    messagesForApi: ChatMessage[],
    asstMsgId: string,
    attachmentsForApi?: ChatRequest['attachments']
  ) => {
    setStreaming(true);
    setStreamError(null);
    setQuotaExhausted(false);
    const controller = new AbortController();
    abortRef.current = controller;
    let ended = false;
    const end = (failed = false) => {
      if (ended) return;
      ended = true;
      finalizeAssistant(agentId, asstMsgId, failed);
    };
    try {
      const request: ChatRequest = { messages: messagesForApi, stream: true };
      if (attachmentsForApi && attachmentsForApi.length > 0) request.attachments = attachmentsForApi;
      for await (const chunk of streamChat(agentId, request, controller.signal)) {
        const delta = chunk.choices?.[0]?.delta?.content;
        if (delta) appendToAssistant(agentId, asstMsgId, delta);
      }
      end();
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        // 用户主动停止生成：保留已输出内容，不报错
        setStreamError(null);
        end();
        return;
      }
      const fatal = handleChatError(err, agent?.name ?? 'Agent');
      end(fatal);
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  /** 选中附件：本地预判类型并生成 chip（图片附 objectURL 预览），发送时才真正上传 */
  const handlePickAttachments = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // 允许重复选择同一文件
    if (files.length === 0) return;
    const accepted: PendingAttachment[] = [];
    for (const file of files) {
      const kind = detectKind(file.name);
      if (!kind) {
        toast(`不支持的文件类型：${file.name}（支持图片 png/jpg/jpeg/webp/gif，文本 txt/md）`, 'error');
        continue;
      }
      accepted.push({
        id: uid(),
        file,
        kind,
        previewUrl: kind === 'image' ? URL.createObjectURL(file) : undefined,
      });
    }
    if (accepted.length > 0) setAttachments((prev) => [...prev, ...accepted]);
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  };

  const handleSend = (text: string) => {
    void doSend(text);
  };

  const doSend = async (text: string) => {
    if (!agent || streaming || uploading) return;
    const trimmed = text.trim();
    const pending = attachments;
    if (!trimmed && pending.length === 0) return;

    // 先上传全部附件拿 upload_id；任一失败则不发送（附件保留可重试）
    let uploadedRefs: Array<{ upload_id: string }> = [];
    let images: Array<{ name: string; url: string }> = [];
    if (pending.length > 0) {
      setUploading(true);
      try {
        const results = await Promise.all(pending.map((p) => uploadAttachment(agent.id, p.file)));
        uploadedRefs = results.map((r) => ({ upload_id: r.upload_id }));
        images = pending
          .map((p, i) => ({ pending: p, result: results[i] }))
          .filter(({ result }) => result.kind === 'image' && !!result)
          .map(({ pending: p, result }) => ({ name: result.name, url: p.previewUrl ?? '' }))
          .filter((img) => !!img.url);
      } catch (err) {
        toast(describeChatError(err) || '附件上传失败，请稍后重试', 'error');
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    const history = messages;
    const userMsg: UiMessage = { id: uid(), role: 'user', content: trimmed, ...(images.length > 0 ? { images } : {}) };
    const asstMsg: UiMessage = { id: uid(), role: 'assistant', content: '', streaming: true };
    const nextList = [...history, userMsg, asstMsg];
    setMessagesByAgent((prev) => ({ ...prev, [agent.id]: nextList }));
    setInput('');
    setAttachments([]);
    const apiHistory: ChatMessage[] = nextList
      .filter((m) => m.id !== asstMsg.id)
      .map(toApiMessage);
    void runCompletion(agent.id, apiHistory, asstMsg.id, uploadedRefs);
  };

  const handleRetry = () => {
    if (!agent || streaming) return;
    const current = getMessages(agent.id);
    // 去掉失败的尾巴（空 assistant 气泡）后重发
    const tail = current[current.length - 1];
    const base = tail && tail.role === 'assistant' && !tail.content ? current.slice(0, -1) : current;
    const asstMsg: UiMessage = { id: uid(), role: 'assistant', content: '', streaming: true };
    setMessagesByAgent((prev) => ({ ...prev, [agent.id]: [...base, asstMsg] }));
    setStreamError(null);
    void runCompletion(agent.id, base.map(toApiMessage), asstMsg.id);
  };

  const handleStop = () => {
    abortRef.current?.abort();
  };

  const handleStartRuntime = async () => {
    if (!agent || starting) return;
    setStarting(true);
    try {
      const result = await startAgent(agent.id);
      if (result.status === 'error') {
        // 启动失败必须给出原因（如运行时镜像缺失），不能只有一句「失败」
        const reason = result.error_message ? `：${result.error_message.slice(0, 160)}` : '';
        toast(`启动「${agent.name}」失败${reason}`, 'error');
      } else {
        toast(`「${agent.name}」已启动，开始对话吧`, 'success');
      }
    } catch (err) {
      const e = err as { response?: { data?: { detail?: unknown }; status?: number } };
      if (e.response?.status === 422) {
        toast('还差一步：请先到右侧「模型」页绑定 LLM 配置，再启动。', 'error');
      } else {
        const detail = e.response?.data?.detail;
        toast(typeof detail === 'string' ? detail : `启动「${agent.name}」失败`, 'error');
      }
    } finally {
      setStarting(false);
      void refreshStatus();
    }
  };

  if (!agent) {
    return (
      <div className="chat-empty">
        <div className="chat-empty__icon">
          <Sparkles size={26} />
        </div>
        <div className="chat-empty__title">从左侧选择一位 Agent，开始练习对话</div>
        <div className="chat-empty__desc">
          选好后，你可以像跟同事聊天一样问它问题；它的「人设」由你在右侧配置。
        </div>
      </div>
    );
  }

  const running = isRuntimeRunning(runtimeStatus);
  const preparing = runtimeStatus === 'pending' || runtimeStatus === 'deploying' || starting;
  const platformModel = runtimeInfo?.model_source === 'platform';

  return (
    <div className="chat">
      {/* 顶栏：Agent 名 + 运行状态 + 平台体验模型徽标 */}
      <div className="chat-head">
        <span className="chat-head__name">{agent.name}</span>
        {platformModel && (
          <span className="badge badge--accent" title="当前使用平台提供的免费体验模型（每日限量）">
            平台体验模型
          </span>
        )}
        <span className={`badge${running ? ' badge--success' : ''}`}>
          <span className={`status-dot status-dot--sm ${running ? 'status-dot--running' : 'status-dot--none'}`} />
          {running ? '运行中' : preparing ? '准备中' : '未启动'}
        </span>
      </div>

      {/* 未运行提示条 */}
      {!running && (
        <div className="chat-banner">
          {preparing ? (
            <>
              <Loader2 size={15} className="spin" />
              <span>正在准备运行时…</span>
            </>
          ) : (
            <>
              <TriangleAlert size={15} />
              <span>
                {runtimeStatus === 'error' && runtimeInfo?.error_message
                  ? `启动失败：${runtimeInfo.error_message.slice(0, 120)}`
                  : '运行时未启动，Agent 暂时无法回复。'}
              </span>
              <button
                type="button"
                className="btn btn-primary chat-banner__btn"
                onClick={() => void handleStartRuntime()}
              >
                <Play size={13} />
                启动服务
              </button>
            </>
          )}
        </div>
      )}

      {/* 平台每日额度相关 429：专属引导卡（toast 已透出后端具体提示） */}
      {quotaExhausted && (
        <div className="chat-banner chat-banner--quota">
          <KeyRound size={15} />
          <span>
            今日平台额度不足。可签到领取今日额度，或到右侧「模型」页配置自己的 API Key 不限量使用。
          </span>
        </div>
      )}

      {/* 消息区 */}
      <div className="chat-messages" ref={listRef}>
        {messages.length === 0 ? (
          historyPending ? (
            <div className="chat-empty chat-empty--inline">
              <Loader2 size={20} className="spin" />
              <div className="chat-empty__desc">正在加载历史对话…</div>
            </div>
          ) : (
          <div className="chat-empty chat-empty--inline">
            <div className="chat-empty__icon">
              <Bot size={24} />
            </div>
            <div className="chat-empty__title">和「{agent.name}」打个招呼吧</div>
            <div className="chat-empty__desc">不知道怎么开场？点下面的问题试试：</div>
            <div className="chat-examples">
              {EXAMPLE_PROMPTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className="chat-example"
                  disabled={streaming}
                  onClick={() => handleSend(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          )
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`chat-row chat-row--${m.role}`}>
              <div className={`chat-bubble${m.role === 'assistant' ? ' chat-bubble--assistant' : ''}`}>
                {m.role === 'assistant' ? (
                  m.content ? (
                    <>
                      <Markdown content={m.content} />
                      {m.streaming && <span className="chat-cursor">▍</span>}
                    </>
                  ) : m.streaming ? (
                    <span className="chat-typing">
                      <Loader2 size={13} className="spin" />
                      正在思考…
                    </span>
                  ) : (
                    <span className="chat-failed">（本次没有收到回复）</span>
                  )
                ) : (
                  <>
                    {m.images && m.images.length > 0 && (
                      <div className="chat-images">
                        {m.images.map((img) => (
                          <img key={img.url} className="chat-image-thumb" src={img.url} alt={img.name} title={img.name} />
                        ))}
                      </div>
                    )}
                    {m.content && <div className="chat-user-text">{m.content}</div>}
                  </>
                )}
              </div>
            </div>
          ))
        )}
        {streamError && (
          <div className="chat-errorbar">
            <WifiOff size={14} />
            <span>{streamError}</span>
            <button type="button" className="btn chat-errorbar__btn" onClick={() => void handleRetry()}>
              重试
            </button>
          </div>
        )}
      </div>

      {/* 输入区：附件 chips + 附件按钮 / 输入框 / 发送 */}
      <div className="chat-composer">
        {attachments.length > 0 && (
          <div className="chat-attachments">
            {attachments.map((a) => (
              <span key={a.id} className="chat-attachment-chip" title={a.file.name}>
                {a.kind === 'image' ? <ImageIcon size={12} /> : <FileText size={12} />}
                <span className="chat-attachment-chip__name">{a.file.name}</span>
                <button
                  type="button"
                  className="chat-attachment-chip__remove"
                  aria-label={`移除附件「${a.file.name}」`}
                  onClick={() => removeAttachment(a.id)}
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="chat-composer__row">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ATTACH_ACCEPT}
            className="file-hidden"
            aria-label="选择附件文件"
            onChange={handlePickAttachments}
          />
          <button
            type="button"
            className="btn btn-icon chat-attach-btn"
            title="添加附件（图片 png/jpg/webp/gif ≤5MB，文本 txt/md ≤1MB）"
            aria-label="添加附件"
            disabled={streaming || uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip size={15} />
          </button>
          <textarea
            className="chat-input"
            rows={1}
            placeholder="输入消息，Enter 发送，Shift+Enter 换行"
            value={input}
            disabled={streaming}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                if (!streaming) handleSend(input);
              }
            }}
          />
          <div className="chat-composer__actions">
            {streaming ? (
              <button type="button" className="btn btn-danger-ghost" onClick={handleStop} title="停止生成">
                <Square size={13} fill="currentColor" />
                停止
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                disabled={(!input.trim() && attachments.length === 0) || uploading}
                onClick={() => handleSend(input)}
                title="发送（Enter）"
              >
                {uploading && <Loader2 size={13} className="spin" />}
                {uploading ? '上传中…' : '发送'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
